import { createContext, lazy, Suspense, useContext, useEffect, useState } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Activity,
  Bell,
  CircleGauge,
  ExternalLink,
  HeartPulse,
  LogOut,
  Menu,
  Moon,
  Radio,
  Settings,
  ShieldCheck,
  Sun,
  X,
} from "lucide-react";
import { api, auth } from "./api";
import { Skeleton } from "./components";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Monitors = lazy(() => import("./pages/Monitors"));
const MonitorForm = lazy(() => import("./pages/MonitorForm"));
const MonitorDetail = lazy(() => import("./pages/MonitorDetail"));
const Incidents = lazy(() => import("./pages/Incidents"));
const Channels = lazy(() => import("./pages/Channels"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const PublicStatus = lazy(() => import("./pages/PublicStatus"));

type User = { id: number; name: string; email: string; role: string };
const AuthContext = createContext<{ user: User | null; setUser: (user: User | null) => void }>({
  user: null,
  setUser: () => undefined,
});
const useAuth = () => useContext(AuthContext);

function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("admin@alivewatch.local");
  const [password, setPassword] = useState("AliveWatch123!");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await api<{ token: string; user: User }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      auth.set(result.token);
      setUser(result.user);
      navigate("/");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "登录失败");
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className="login-page">
      <section className="login-story">
        <div className="brand brand-light">
          <span className="brand-mark">
            <HeartPulse />
          </span>
          <span>AliveWatch</span>
        </div>
        <div>
          <p className="eyebrow light">让每一次故障都有迹可循</p>
          <h1>
            持续感知服务的
            <br />
            每一次心跳。
          </h1>
          <p>从网站、API 到端口与网络节点，AliveWatch 帮你在用户发现问题之前掌握异常。</p>
        </div>
        <div className="trust-row">
          <ShieldCheck />
          <span>自托管 · 数据自主 · 开源透明</span>
        </div>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div className="mobile-brand">
            <HeartPulse />
            AliveWatch
          </div>
          <div>
            <p className="eyebrow">欢迎回来</p>
            <h2>登录控制台</h2>
            <p className="muted">查看你的服务健康状态与最新事件</p>
          </div>
          {error && <div className="form-error">{error}</div>}
          <label>
            邮箱地址
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            密码
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <div className="login-meta">
            <label className="check">
              <input type="checkbox" />
              保持登录
            </label>
            <span>默认账号已预填</span>
          </div>
          <button className="button primary large" disabled={loading}>
            {loading ? "正在验证…" : "进入 AliveWatch"}
          </button>
          <a className="public-link" href="/status">
            查看公开状态页 <ExternalLink size={14} />
          </a>
        </form>
      </section>
    </main>
  );
}

const nav = [
  ["/", "总览", CircleGauge],
  ["/monitors", "监控项目", Radio],
  ["/incidents", "故障事件", Activity],
  ["/channels", "通知渠道", Bell],
  ["/settings", "系统设置", Settings],
] as const;

function Shell() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("alivewatch_theme") !== "light");
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("alivewatch_theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => setOpen(false), [location.pathname]);
  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <div className="sidebar-top">
          <div className="brand">
            <span className="brand-mark">
              <HeartPulse />
            </span>
            <span>AliveWatch</span>
          </div>
          <button
            className="icon-button sidebar-close"
            onClick={() => setOpen(false)}
            aria-label="关闭导航"
          >
            <X />
          </button>
        </div>
        <nav>
          {nav.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/"}>
              <Icon />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <a href="/status" target="_blank">
            <ExternalLink />
            公开状态页
          </a>
          <div className="user-card">
            <span className="avatar">{user?.name?.slice(0, 1) ?? "A"}</span>
            <div>
              <strong>{user?.name ?? "管理员"}</strong>
              <small>{user?.email}</small>
            </div>
            <button
              className="icon-button"
              title="退出登录"
              onClick={() => {
                auth.clear();
                setUser(null);
                window.location.assign("/login");
              }}
            >
              <LogOut />
            </button>
          </div>
        </div>
      </aside>
      {open && <button className="scrim" onClick={() => setOpen(false)} aria-label="关闭导航" />}
      <div className="main-column">
        <header className="topbar">
          <button
            className="icon-button menu-button"
            onClick={() => setOpen(true)}
            aria-label="打开导航"
          >
            <Menu />
          </button>
          <div className="live-pill">
            <span />
            监控引擎运行中
          </div>
          <div className="top-actions">
            <button className="icon-button" onClick={() => setDark(!dark)} aria-label="切换主题">
              {dark ? <Sun /> : <Moon />}
            </button>
            <a className="button compact primary" href="/monitors/new">
              ＋ 新建监控
            </a>
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Protected() {
  return auth.token ? <Outlet /> : <Navigate to="/login" replace />;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  useEffect(() => {
    if (auth.token)
      api<{ user: User }>("/api/auth/me")
        .then((value) => setUser(value.user))
        .catch(() => auth.clear());
  }, []);
  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <Suspense
        fallback={
          <div className="route-loading">
            <Skeleton rows={5} />
          </div>
        }
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/status" element={<PublicStatus />} />
          <Route element={<Protected />}>
            <Route element={<Shell />}>
              <Route index element={<Dashboard />} />
              <Route path="monitors" element={<Monitors />} />
              <Route path="monitors/new" element={<MonitorForm />} />
              <Route path="monitors/:id/edit" element={<MonitorForm />} />
              <Route path="monitors/:id" element={<MonitorDetail />} />
              <Route path="incidents" element={<Incidents />} />
              <Route path="channels" element={<Channels />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AuthContext.Provider>
  );
}
